<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, watch } from 'vue';
import { mmToPixels, pixelsToMm } from '@/utils/unitsUtils';
import { calculateFontSizesFromDimensions, calculateInitTextValues } from '@/utils/generalMathUtils';
import CButton from '@/components/shared/CButton/CButton.vue';
import CInput, { type InputValue } from '@/components/shared/CInput';
import type { CanvasEx } from '@/typings/canvas';
import type {
    ActiveSelection,
    BaseCanvasElement,
    BoundedTextElement,
    CanvasElement,
    CanvasOrSelection,
    ImagePlaceholderElement,
    PathITextElement,
    TextTransformElement
} from '@/typings/customilyElements/canvasElements';

const props = withDefaults(
    defineProps<{
        canvas: CanvasEx;
        canvasElement: CanvasOrSelection;
        editMode?: boolean;
        lockRatio?: boolean;
        /**
         * Editing width/height cascades to the other dimension keeping the current
         * aspect ratio of the bounding box (step 2 behavior)
         */
        linkedRatio?: boolean;
        bulkEdit?: boolean;
        units?: 'mm' | 'px';
        perspective?: boolean;
        skew?: boolean;
    }>(),
    {
        units: 'mm'
    }
);

const emit = defineEmits<{
    (e: 'arrange-layer', element: CanvasOrSelection, position: 'front' | 'forward' | 'back' | 'backward'): void;
}>();

const model = reactive({
    width: 0 as number | undefined,
    height: 0 as number | undefined,
    centerX: 0 as number | undefined,
    centerY: 0 as number | undefined,
    rotation: 0 as number | undefined,
    skewX: undefined as number | undefined,
    skewY: undefined as number | undefined,
    locked: false,
    isOverlay: false,
    perspectiveActive: false,
    perspectiveEnabled: false,
    hasInitWidth: false
});

const isActiveSelectionElement = (element: CanvasOrSelection): element is ActiveSelection<CanvasElement> =>
    element.type === 'activeSelection';

const isPathITextElement = (element: CanvasOrSelection): element is PathITextElement => element.type === 'path-itext';

const isBaseTextElement = (
    element: CanvasOrSelection
): element is BoundedTextElement | TextTransformElement | PathITextElement =>
    ['bounded-text', 'text-transform', 'path-itext'].includes(element.type ?? '');

const canvasDpi = computed(() => props.canvas.DPI);
const isBulkMode = computed(() => !!props.bulkEdit && props.canvasElement?.type === 'activeSelection');

const fromDisplayUnit = (px: number): number => {
    const value = props.units === 'mm' ? pixelsToMm(px, canvasDpi.value) : px;
    return Number(value.toFixed(2));
};
const toDisplayUnit = (value: number): number => {
    const result = props.units === 'mm' ? mmToPixels(value, canvasDpi.value) : value;
    return Number(result.toFixed(2));
};

/**
 * In bulk mode the UI variants (skew, diameters, etc.) follow the type of the selected
 * objects instead of the ActiveSelection wrapper
 */
const elementType = computed(() => {
    const element = props.canvasElement;
    if (!element) return undefined;

    return isBulkMode.value && isActiveSelectionElement(element) ? element.getObjects()?.[0]?.type : element.type;
});
const isTextTransform = computed(() => elementType.value === 'text-transform');
const isPathIText = computed(() => elementType.value === 'path-itext');
const isBoundedText = computed(() => elementType.value === 'bounded-text');
const showSkew = computed(() => isBoundedText.value || (props.skew && !isTextTransform.value));
const showFlip = computed(() => !isPathIText.value && !isTextTransform.value);
const showPerspective = computed(
    () => props.perspective && !isBulkMode.value && elementType.value === 'image-placeholder'
);

const disabled = computed(() => ({
    common: model.locked,
    arrange: model.locked || model.isOverlay,
    flip: model.locked || isTextTransform.value
}));

const placeholderFor = (value?: number) => (value === undefined ? 'Mixed' : '0');

/**
 * After a bulk apply, temporaryDiscardActiveObject recreates the ActiveSelection without firing
 * selection events, so the prop reference can be stale; prefer the current selection on the canvas
 */
const resolveElement = (): CanvasOrSelection | undefined => {
    if (!isBulkMode.value) return props.canvasElement;

    const active = props.canvas.getActiveObject() as CanvasOrSelection | null;
    if (active && isActiveSelectionElement(active)) return active;
};

const uniformNumber = (values: number[]): number | undefined => {
    const [first] = values;
    return values.every((value) => value === first) ? first : undefined;
};

const transformPoint = (point: { x: number; y: number }, matrix: number[]) => ({
    x: matrix[0] * point.x + matrix[2] * point.y + matrix[4],
    y: matrix[1] * point.x + matrix[3] * point.y + matrix[5]
});

const getDimensionPx = (element: CanvasOrSelection, dimension: 'width' | 'height'): number => {
    if (isActiveSelectionElement(element)) {
        return dimension === 'width' ? element.getScaledWidth() : element.getScaledHeight();
    }
    if (isPathITextElement(element)) {
        return (dimension === 'width' ? element.horizontalDiameter : element.verticalDiameter) || 0;
    }
    return dimension === 'width' ? element.getWidth() : element.getHeight();
};

const syncDimensionFromElement = (dimension: 'width' | 'height') => {
    const element = resolveElement();
    if (!element) return;

    if (isBulkMode.value && isActiveSelectionElement(element)) {
        const pixels = uniformNumber(element.getObjects().map((object) => getDimensionPx(object, dimension)));
        model[dimension] = pixels === undefined ? undefined : fromDisplayUnit(pixels);
        return;
    }

    model[dimension] = fromDisplayUnit(getDimensionPx(element, dimension));
};

const loadBulkModel = (selection: ActiveSelection<CanvasElement>) => {
    const objects = selection.getObjects();
    const first = objects[0];
    if (!first) return;

    // Object coordinates are group-relative; compare and display them in absolute canvas space
    const groupMatrix = selection.calcTransformMatrix();
    const points = objects.map((object) => transformPoint({ x: object.left || 0, y: object.top || 0 }, groupMatrix));
    const centerX = uniformNumber(points.map((point) => point.x));
    const centerY = uniformNumber(points.map((point) => point.y));
    model.centerX = centerX === undefined ? undefined : fromDisplayUnit(centerX);
    model.centerY = centerY === undefined ? undefined : fromDisplayUnit(centerY);

    const angle = uniformNumber(objects.map((object) => object.angle || 0));
    model.rotation = angle === undefined ? undefined : angle + (selection.angle || 0);
    if (model.rotation) model.rotation = Number(model.rotation.toFixed(2))
    model.skewX = uniformNumber(objects.map((object) => object.skewX || 0));
    model.skewY = uniformNumber(objects.map((object) => object.skewY || 0));
    model.locked = !!first.locked;
    model.isOverlay = false;
};

const loadModel = () => {
    const element = resolveElement();
    if (!element) return;

    syncDimensionFromElement('width');
    syncDimensionFromElement('height');
    model.rotation = Number(element.angle?.toFixed(2) || 0);

    const image = element.type === 'image-placeholder' ? element : undefined;
    model.perspectiveActive = !!image?.__perspectiveMode;
    model.perspectiveEnabled = !!image?.enablePerspective;
    model.hasInitWidth = !!image?.initWidth;

    if (isActiveSelectionElement(element)) {
        if (isBulkMode.value) {
            loadBulkModel(element);
            return;
        }

        const point = element.getPointByOrigin('center', 'center');
        model.centerX = fromDisplayUnit(point.x);
        model.centerY = fromDisplayUnit(point.y);
        model.skewX = model.skewY = undefined;
        model.locked = false;
        model.isOverlay = false;
        return;
    }

    model.centerX = fromDisplayUnit(element.left || 0);
    model.centerY = fromDisplayUnit(element.top || 0);
    model.skewX = element.skewX || 0;
    model.skewY = element.skewY || 0;
    model.locked = element.locked;
    model.isOverlay = element.isOverlay;
};

let internalUpdate = false;
const onElementModified = () => {
    // Skip model reloads triggered by our own writes so we don't clobber the value being typed
    if (internalUpdate) return;
    loadModel();
};

const attachListeners = (element: CanvasOrSelection) => {
    if (isBulkMode.value) {
        // The selection wrapper is recreated on every bulk apply; listen at canvas level instead
        props.canvas.on('object:modified', onElementModified);
        props.canvas.on('customilyActiveSelection:modified', onElementModified);
        return;
    }
    element.on('modified', onElementModified);
};

const detachListeners = (element?: CanvasOrSelection | null) => {
    element?.off('modified', onElementModified);
    props.canvas?.off('object:modified', onElementModified);
    props.canvas?.off('customilyActiveSelection:modified', onElementModified);
};

watch(
    () => props.canvasElement,
    (element, oldElement) => {
        detachListeners(oldElement);
        if (!element) return;
        attachListeners(element);
        loadModel();
    },
    { immediate: true }
);

onBeforeUnmount(() => {
    detachListeners(props.canvasElement);
});

const applyUpdate = (
    updater: (element: CanvasOrSelection) => void,
    options: { resize?: boolean; textMask?: boolean; selectionSkip?: Record<string, boolean> } = {}
) => {
    const element = resolveElement();
    if (!element) return;

    internalUpdate = true;
    try {
        const isActiveSelection = isActiveSelectionElement(element);
        if (isBulkMode.value && isActiveSelection) {
            const objects = element.getObjects();
            // Objects inside an ActiveSelection have group-relative coordinates; discard the
            // selection so updates operate on absolute values, then it gets recreated
            props.canvas.temporaryDiscardActiveObject(() => {
                for (const object of objects) {
                    updater(object);
                    if (props.editMode && options.resize) {
                        object.editedSize = true;
                    }
                    object.setCoords();
                    if (options.textMask) {
                        props.canvas.fire('object:handle:textmask', { target: object });
                    }
                    object.fire('modified');
                }
            });
            props.canvas.requestRenderAll();
            // Lets the AngularJS side re-read canvas.getActiveObject() and refresh its group state
            props.canvas.fire('customilyActiveSelection:modified', options.selectionSkip ?? {});
            return;
        }

        updater(element);
        if (props.editMode && options.resize && !isActiveSelection) {
            element.editedSize = true;
        }
        element.setCoords();
        props.canvas.requestRenderAll();
        if (isActiveSelection && options.selectionSkip) {
            props.canvas.fire('customilyActiveSelection:modified', options.selectionSkip);
        }
        if (options.textMask) {
            props.canvas.fire('object:handle:textmask', { target: element });
        }
        // Keep the AngularJS side (TextService.loadTextData, current font size, etc.) in sync
        element.fire('modified');
    } finally {
        internalUpdate = false;
    }
};

const updateCenterX = (value: InputValue) => {
    const centerXValue = Number(value);
    model.centerX = centerXValue;
    applyUpdate(
        (element) => {
            const centerX = toDisplayUnit(centerXValue);
            if (isActiveSelectionElement(element)) {
                const point = element.getPointByOrigin('center', 'center');
                point.x = centerX;
                element.setPositionByOrigin(point, 'center', 'center');
                return;
            }
            const base: BaseCanvasElement = element;
            base.set('left', centerX);
        },
        { textMask: true, selectionSkip: { skipCenterX: true } }
    );
};

const updateCenterY = (value: InputValue) => {
    const centerYValue = Number(value);
    model.centerY = centerYValue;
    applyUpdate(
        (element) => {
            const centerY = toDisplayUnit(centerYValue);
            if (isActiveSelectionElement(element)) {
                const point = element.getPointByOrigin('center', 'center');
                point.y = centerY;
                element.setPositionByOrigin(point, 'center', 'center');
                return;
            }
            (element as BaseCanvasElement).set('top', centerY);
        },
        { textMask: true, selectionSkip: { skipCenterY: true } }
    );
};

const updateRotation = (value: InputValue) => {
    const rotation = Number(value);
    model.rotation = rotation;
    applyUpdate(
        (element) => {
            if (isActiveSelectionElement(element)) {
                element.rotate(rotation);
                return;
            }
            const base: BaseCanvasElement = element;
            if (base.setRotate) {
                base.setRotate(rotation);
                return;
            }
            base.set('angle', rotation);
        },
        { textMask: true, selectionSkip: { skipRotation: true } }
    );
};

const updateSkew = (axis: 'x' | 'y', value: InputValue) => {
    const degrees = Number(value);
    const key = axis === 'x' ? 'skewX' : 'skewY';
    model[key] = degrees;

    applyUpdate((element) => {
        if (isActiveSelectionElement(element)) return;
        const base: BaseCanvasElement = element;
        base.set(key, degrees);
    });
};

const applyWidthPx = (element: CanvasOrSelection, widthPx: number) => {
    if (isActiveSelectionElement(element)) {
        // The static width of an ActiveSelection stays fixed while scaling; use it as reference
        element.scale(widthPx / (element.width || 1));
        return;
    }
    if (isPathITextElement(element)) {
        element.set('horizontalDiameter', widthPx);
        return;
    }

    const base: BaseCanvasElement = element;
    const width = widthPx / (base.scaleX || 1);
    if (base.setWidth) {
        const keepRatio = props.lockRatio && base.type === 'image-placeholder' && base.hasInitValues?.();
        base.setWidth(width, keepRatio);
        return;
    }
    base.set('width', width);
};

const applyHeightPx = (element: CanvasOrSelection, heightPx: number) => {
    if (isActiveSelectionElement(element)) {
        // The static height of an ActiveSelection stays fixed while scaling; use it as reference
        element.scale(heightPx / (element.height || 1));
        return;
    }
    if (isPathITextElement(element)) {
        element.set('verticalDiameter', heightPx);
        return;
    }

    const base: BaseCanvasElement = element;
    const height = heightPx / (base.scaleY || 1);
    if (base.setHeight) {
        const keepRatio = props.lockRatio && base.type === 'image-placeholder' && base.hasInitValues?.();
        base.setHeight(height, keepRatio);
        return;
    }
    base.set('height', height);
};

const getElementRatio = (element: CanvasOrSelection): number => {
    const width = getDimensionPx(element, 'width');
    const height = getDimensionPx(element, 'height');
    return height > 0 ? width / height : 0;
};

/**
 * After a linked (proportional) resize, texts must rescale their font size limits and,
 * when user-editable, their initial position/size (step 2 behavior)
 */
const applyLinkedTextAdjustments = (element: CanvasOrSelection) => {
    if (!isBaseTextElement(element) || !element.originalWidth) return;

    const sizes = calculateFontSizesFromDimensions(
        element.getWidth(),
        element.originalWidth,
        element.originalMinSizePx ?? 0,
        element.originalMaxSizePx ?? 0
    );
    element.setMinSizePx?.(sizes.min);
    element.setMaxSizePx?.(sizes.max);

    if (element.allowEdit && element.originalInitCenterX != null) {
        const values = calculateInitTextValues(
            element.getWidth(),
            element.originalWidth,
            element.originalInitCenterX,
            element.originalInitCenterY ?? 0,
            element.originalInitFontSize ?? 0
        );
        element.setInitPositionAndSize?.(values.initCenterX, values.initCenterY, values.initFontSize);
    }
};

const updateWidth = (value: InputValue) => {
    const widthValue = Number(value);
    model.width = widthValue;
    applyUpdate(
        (element) => {
            const widthPx = toDisplayUnit(widthValue);
            // The ratio must be captured before resizing
            const ratio = getElementRatio(element);
            applyWidthPx(element, widthPx);

            if (props.linkedRatio && ratio && !isActiveSelectionElement(element)) {
                applyHeightPx(element, widthPx / ratio);
                applyLinkedTextAdjustments(element);
            }
        },
        { resize: true, textMask: !isPathIText.value, selectionSkip: { skipWidth: true } }
    );
    syncDimensionFromElement('height');
};

const updateHeight = (value: InputValue) => {
    const heightValue = Number(value);
    model.height = heightValue;
    applyUpdate(
        (element) => {
            const heightPx = toDisplayUnit(heightValue);
            // The ratio must be captured before resizing
            const ratio = getElementRatio(element);
            applyHeightPx(element, heightPx);

            if (props.linkedRatio && ratio && !isActiveSelectionElement(element)) {
                applyWidthPx(element, heightPx * ratio);
                applyLinkedTextAdjustments(element);
            }
        },
        { resize: true, textMask: !isPathIText.value, selectionSkip: { skipHeight: true } }
    );
    syncDimensionFromElement('width');
};

const flipElement = (axis: 'x' | 'y' = 'x') => {
    const element = props.canvasElement;
    if (!element) return;

    const objects = isActiveSelectionElement(element) ? element.getObjects() : [element];

    for (const object of objects) {
        const flip = object.getFlip();
        const flipX = axis == 'x' ? !flip.flipX : flip.flipX;
        const flipY = axis == 'y' ? !flip.flipY : flip.flipY;

        object.setFlip(flipX!, flipY!);
        object.setCoords();
    }

    props.canvas.requestRenderAll();
};

const arrangeElement = (position: 'front' | 'forward' | 'back' | 'backward') => {
    const element = props.canvasElement;
    if (!element) return;
    emit('arrange-layer', element, position);
};

const togglePerspective = () => {
    const element = resolveElement() as ImagePlaceholderElement;
    if (!element?.togglePerspective) return;

    element.enablePerspective = true;
    element.togglePerspective(!element.__perspectiveMode);
    model.perspectiveActive = !!element.__perspectiveMode;
    model.perspectiveEnabled = true;
};

const resetPerspective = () => {
    const element = resolveElement() as ImagePlaceholderElement;
    if (!element?._removePerspective) return;

    element._removePerspective();
    model.perspectiveActive = !!element.__perspectiveMode;
    model.perspectiveEnabled = false;
};
</script>

<template>
    <div class="element-transform-wrapper">
        <section>
            <h6>Dimensions</h6>
            <div class="border-bottom" />
            <div class="input-container">
                <c-input
                    class="flex-fill"
                    :value="model.width"
                    :label="`Width (${units})`"
                    type="decimal"
                    :placeholder="placeholderFor(model.width)"
                    :min="1"
                    :max="9999999"
                    :disabled="disabled.common"
                    @input="updateWidth"
                />
                <font-awesome-icon v-if="linkedRatio" class="mt-3" :icon="['far', 'link']" />
                <c-input
                    class="flex-fill"
                    :value="model.height"
                    :label="`Height (${units})`"
                    type="decimal"
                    :placeholder="placeholderFor(model.height)"
                    :min="1"
                    :max="9999999"
                    :disabled="disabled.common"
                    @input="updateHeight"
                />
            </div>
        </section>
        <section>
            <h6>Position</h6>
            <div class="border-bottom" />
            <div class="input-container">
                <c-input
                    class="flex-fill"
                    :value="model.centerX"
                    :label="`Position x (${units})`"
                    type="decimal"
                    :placeholder="placeholderFor(model.centerX)"
                    :min="-9999999"
                    :max="9999999"
                    :disabled="disabled.common"
                    @input="updateCenterX"
                />
                <c-input
                    class="flex-fill"
                    :value="model.centerY"
                    :label="`Position y (${units})`"
                    type="decimal"
                    :placeholder="placeholderFor(model.centerY)"
                    :min="-9999999"
                    :max="9999999"
                    :disabled="disabled.common"
                    @input="updateCenterY"
                />
            </div>
            <div class="input-container">
                <c-input
                    class="flex-fill"
                    :value="model.rotation"
                    label="Rotation (dg)"
                    type="decimal"
                    :placeholder="placeholderFor(model.rotation)"
                    :min="-360"
                    :max="360"
                    :disabled="disabled.common"
                    @input="updateRotation"
                />
                <template v-if="showSkew">
                    <c-input
                        class="flex-fill"
                        :value="model.skewX"
                        label="Skew Horizontal (dg)"
                        type="decimal"
                        :placeholder="placeholderFor(model.skewX)"
                        :min="-80"
                        :max="80"
                        :disabled="disabled.common"
                        @input="updateSkew('x', $event)"
                    />
                    <c-input
                        class="flex-fill"
                        :value="model.skewY"
                        label="Skew vertical (dg)"
                        type="decimal"
                        :placeholder="placeholderFor(model.skewY)"
                        :min="-80"
                        :max="80"
                        :disabled="disabled.common"
                        @input="updateSkew('y', $event)"
                    />
                </template>
            </div>
        </section>
        <section v-if="!isBulkMode" class="flex-row">
            <div>
                <label class="m-0">Arrange Layers</label>
                <div class="input-container">
                    <c-button
                        v-tippy
                        content="Bring to front"
                        size="icon"
                        :icon="['far', 'bring-front']"
                        :disabled="disabled.arrange"
                        @click="arrangeElement('front')"
                    />
                    <c-button
                        v-tippy
                        content="Bring forward"
                        size="icon"
                        :icon="['far', 'bring-forward']"
                        :disabled="disabled.arrange"
                        @click="arrangeElement('forward')"
                    />
                    <c-button
                        v-tippy
                        content="Send backward"
                        size="icon"
                        :icon="['far', 'send-backward']"
                        :disabled="disabled.arrange"
                        @click="arrangeElement('backward')"
                    />
                    <c-button
                        v-tippy
                        content="Send to back"
                        size="icon"
                        :icon="['far', 'send-back']"
                        :disabled="disabled.arrange"
                        @click="arrangeElement('back')"
                    />
                </div>
            </div>
            <template v-if="showFlip">
                <div class="border-left" />
                <div>
                    <label class="m-0">Flip</label>
                    <div class="input-container">
                        <c-button
                            v-tippy
                            content="Flip Horizontal"
                            size="icon"
                            :icon="['far', 'reflect-horizontal']"
                            :disabled="disabled.flip"
                            @click="flipElement('x')"
                        />
                        <c-button
                            v-tippy
                            content="Flip Vertial"
                            size="icon"
                            :icon="['far', 'reflect-vertical']"
                            :disabled="disabled.flip"
                            @click="flipElement('y')"
                        />
                    </div>
                </div>
            </template>
            <div v-if="showPerspective" class="align-self-end">
                <c-button
                    text="Perspective"
                    :variant="model.perspectiveActive ? 'outline-primary' : 'light'"
                    :icon="['fak', 'perspective']"
                    :disabled="model.hasInitWidth"
                    @click="togglePerspective"
                />
                <c-button
                    v-if="model.perspectiveEnabled"
                    v-tippy
                    content="Reset perspective"
                    class="ml-2"
                    variant="light"
                    size="icon"
                    :icon="['fas', 'rotate-left']"
                    @click="resetPerspective"
                />
            </div>
        </section>
    </div>
</template>

<style lang="scss" scoped>
.element-transform-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    section {
        display: flex;
        flex-direction: column;
        gap: 0.625rem;
        h6 {
            font-size: 0.875rem;
            margin: 0;
        }
        .input-container {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            .c-input-wrapper {
                flex: 1;
            }
        }
    }
}
</style>
